import {Addressable} from './api-common.ts';
import {BASE_API_URL} from '../envs.ts';

import type {FeatureLike} from 'ol/Feature';
import {replaceAll} from '../helpers/string.ts';
import type {LandCode, LandLayers} from '../layers/layerTypes.ts';
import type {LanguageCode} from '../i18n.ts';
import {apiCodeToLandCode} from '../layers/layerHelpers.ts';

export type SubcategoryCode =
  | 'contact'
  | 'directions'
  | 'hints'
  | 'protected_area'
  | 'public_transport'
  | 'safety'
  | 'season'
  | 'signalization';

export class GoodToKnowBase extends Addressable {
  id: number;
  title: string;
  text?: string;
  photo?: string;
  photoUrls: Record<string, string>;
  photoBig?: string;
  photoSmall?: string;
  photoMaster?: string;
  photoAbstract?: string;
  file?: string;
  subCategory?: SubcategoryCode;
  urlLink?: string;
  urlText?: string;

  constructor(data: GoodToKnowBase) {
    super(data);
    Object.assign(this, <GoodToKnowBase>data);
  }
}

export class Arrival extends GoodToKnowBase {
  station: number;
  stationName: string;

  constructor(data: Arrival) {
    super(data);
    Object.assign(this, <Arrival>data);
  }
}

export class Direction extends GoodToKnowBase {
  handicapQuality: string;

  constructor(data: Direction) {
    super(data);
    Object.assign(this, <Direction>data);
  }
}

export class Detour extends GoodToKnowBase {
  land: LandCode;
  duration?: string;
  start?: string;
  end?: string;
  reason?: string;
  state?: string;
  dataSource?: string;
  refid: number;
  hikingNetwork?: boolean;

  constructor(data: FeatureLike | Detour) {
    let detour: Detour;
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      const land: LandLayers = data.get('land');
      detour = {
        id: <number>data.getId(),
        refid: <number>data.getId(),
        title: data.get('title'),
        land: apiCodeToLandCode[land],
        text: data.get('abstract'),
        reason: data.get('reason'),
        state: data.get('state_validate'),
        urlLink: data.get('url1_link'),
        urlText: data.get('url1_text'),
        dataSource: data.get('content_provider'),
        file: data.get('file'),
        duration: data.get('duration'),
        start: data.get('date_start'),
        end: data.get('date_end'),
        photoUrls: data.get('photo_img_urls'),
      };
    } else {
      data = <Detour>data;
      if (data.land in apiCodeToLandCode)
        data.land = apiCodeToLandCode[<LandLayers>data.land];
      else if (data.hikingNetwork === true)
        data.land = apiCodeToLandCode['wanderland'];
      detour = data;
    }

    super(detour);
    Object.assign(this, detour);

    if (this.start)
      this.start = replaceAll(
        new Date(this.start).toLocaleDateString('en-GB'),
        '/',
        '.',
      );
    if (this.end)
      this.end = replaceAll(
        new Date(this.end).toLocaleDateString('en-GB'),
        '/',
        '.',
      );
  }
}

export async function fetchArrivals(
  ids: number[],
  lang: LanguageCode,
): Promise<Arrival[]> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/goodtoknow/arrivals/${ids.join(',')}?lang=${lang}`,
  );
  const arrivals: Arrival[] = await response.json();
  return arrivals.map((a) => new Arrival(a));
}

export async function fetchDirections(
  ids: number[],
  lang: LanguageCode,
): Promise<Direction[]> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/goodtoknow/directions/${ids.join(',')}?lang=${lang}`,
  );
  const directions: Direction[] = await response.json();
  return directions.map((d) => new Direction(d));
}

export async function fetchDetours(
  ids: number[],
  lang: LanguageCode,
): Promise<Detour[]> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/goodtoknow/detours/${ids.join(',')}?lang=${lang}`,
  );
  const detours: Detour[] = await response.json();
  return detours.map((d) => new Detour(d));
}
